import { ref } from 'vue'
import throttle from 'lodash/throttle'
import debounce from 'lodash/debounce'
import sortBy from 'lodash/sortBy'

// enables debug mode for creating visible borders of drop areas
const debug = false

// enables debug mode for drag mouse down
const debugMouseDown = false

const throttleEvent = function (event, callback, interval) {
  const throttled = throttle(callback, interval)
  const outer = (ev) => {
    ev.preventDefault()
    ev.stopPropagation()
    throttled(ev)
  }

  window.document.addEventListener(event, outer)

  return () => {
    throttled.cancel()
    window.document.removeEventListener(event, outer)
  }
}

const debounceEvent = function (event, callback, interval) {
  const debounced = debounce(callback, interval)
  const outer = (ev) => {
    ev.preventDefault()
    ev.stopPropagation()
    debounced(ev)
  }

  window.document.addEventListener(event, outer)

  return () => {
    debounced.cancel()
    window.document.removeEventListener(event, outer)
  }
}

// debug function for creating visible borders of drop areas
let debugRenderCounter = 0
const debugRedrawBoundsRect = function (visibleElements) {
  if (!debug) return

  debugRenderCounter++
  
  let debugDiv = document.getElementById('debug-place')
  if (!debugDiv) {
    debugDiv = document.createElement('DIV')
    debugDiv.id = 'debug-place'
    window.document.body.appendChild(debugDiv)
  }

  while (debugDiv.firstChild) {
    debugDiv.removeChild(debugDiv.lastChild);
  }

  for (let i = 0; i < visibleElements.length; i++) {
    const child = visibleElements[i];
    const el = document.createElement('DIV')
    el.classList.add('border', 'border-1', 'border-red-600')
    
    el.style.position = 'fixed'
    el.style.width = child.rect.width + 'px' 
    el.style.height = child.rect.height + 'px' 
    el.style.top = child.rect.y + 'px'
    el.style.left = child.rect.x + 'px'
    el.style.zIndex = 1000
    el.style.pointerEvents = 'none'
    el.textContent = debugRenderCounter + ' --- ' + child.index

    debugDiv.appendChild(el)
  }
}

const AwDnd = {
  draggedItem: ref(null), // dragged item data
  draggedElement: null, // dragged dom element under mouse
  dropAreas: new Map(), // map with dropareas vue component instances
  visibleElementsObserver: null,
  visibleElements: new Map(), // map with currently visible elements
  visibleElementsData: [],
  visibleElementsDataValid: true, // is visible data is valid?
  dropArea: null, // selected drop area
  originalDropAreaId: null, // starting drop area ID
  currentDropAreaId: ref(null), // current drop area ID
  index: null, // selected placeholder index
  overElement: null, // last element which mouse was over
  overElementId: ref(null), // last element id which mouse was over
  mouseX: null,
  mouseY: null,
  mouseDir: 0, // mouse direction +1 / 0 / -1

  mouseMoveListener: null,
  touchMoveListener: null,
  scrollListener: null,
  debouncedUpdatePlaceholderIndex: null,

  documentHeight: null,
  windowHeight: null,
  autoScrollInterval: null,
  
  debug,
  debugMouseDown,

  dragStart(ev, item, element, originalDropAreaId) {
    this.draggedElement = element
    
    // init visible items intersection observer
    this.visibleElementsObserver = new IntersectionObserver((entries) => {
      for (let i = 0; i < entries.length; i++) {
        const element = entries[i];
        if (element.isIntersecting) {
          this.visibleElements.set(element.target, {})
        } else {
          this.visibleElements.delete(element.target)
        }
      }
      
      this.invalidVisibleElementsData(this.visibleElementsData.length === 0)
      
      // do the first init by el dolly
      if (this.mouseX === null && this.mouseY === null) {
        if (this.dropAreas.has(this.draggedElement.parentNode)) {
          this.debouncedUpdatePlaceholderIndex({
            index: this.getElementIndex(element.awDndClonedElement, this.draggedElement.parentNode),
            parent: this.draggedElement.parentNode
          })

          this.dropArea = this.draggedElement.parentNode
        }
      }
    }, { threshold: 0 })

    // register debounce update placeholder
    this.debouncedUpdatePlaceholderIndex = debounce((child) => {
      if (this.index == child.index) {
        return
      }
      
      const dropArea = this.dropAreas.get(child.parent)
      
      this.index = child.index
      dropArea.placeholderIndex.value = child.index
      
      if (dropArea.placeholderEl && child.index === null) {
        dropArea.placeholderEl.awDndPlaceholder = false
        dropArea.placeholderEl = null
      }
      this.invalidVisibleElementsData(false)
    }, 10)

    // init mouse move event listeners
    if (!debugMouseDown) {
      this.mouseMoveListener = throttleEvent('mousemove', (ev) => { this.mouseMove(ev) }, 30)
      this.touchMoveListener = throttleEvent('touchmove', (ev) => { this.mouseMove(ev) }, 30)
      this.scrollListener = debounceEvent('scroll', () => { this.invalidVisibleElementsData(); this.checkIntersection() }, 30)
    }

    this.documentHeight = document.documentElement.getBoundingClientRect().height
    this.windowHeight = window.innerHeight

    // set dragged element data
    this.draggedItem.value = item
    this.originalDropAreaId = originalDropAreaId
  },

  invalidVisibleElementsData(recalc) {
    this.visibleElementsDataValid = false

    if (recalc === true || recalc === undefined) {
      this.recalculateVisibleElements()
    }
  },

  getElementIndex(element, parent) {    
    /*
    if (this.dropArea === parent && index > this.index) {
      index--
    }
    */

    // if empty dropareas index is 0
    // else set index to elemnts index in draggable elements

    return element.awDndDropArea ? 0 : this.dropAreas.get(parent).getDraggableElements().indexOf(element)
  },

  recalculateVisibleElements() {
    if (this.visibleElementsDataValid) {
      return
    }

    const visibleElementsData = []
    const visibleElementsByParent = new Map()
    for (const iterator of this.visibleElements.entries()) {
      const element = iterator[0]
      //const child = iterator[1]
      const parent = element.awDndDropArea ? element.awDndDropArea : element.parentNode

      // detect vue transition animation classes
      if (!parent || (element._vtc && element._vtc.size)) {
        return
      }

      const index = this.getElementIndex(element, parent)
      const rect = element.getBoundingClientRect()
      const dto = {
        rect,
        parent,
        index,
        element
      }

      if (!visibleElementsByParent.has(parent)) {
        visibleElementsByParent.set(parent, [ dto ])
      } else {
        visibleElementsByParent.get(parent).push(dto)
      }
    }

    visibleElementsByParent.forEach((children, parent) => {
      // sort children by position from top and from left
      const sortedChildren = sortBy(children, [(child) => child.rect.y, (child) => child.rect.x])
      const dropArea = this.dropAreas.get(parent)

      // loop through sorted children
      for (let i = 0; i < sortedChildren.length; i++) {
        const child = sortedChildren[i]
        const nextChild = i + 1 < sortedChildren.length ? sortedChildren[i + 1] : null

        // check if nextChild is under child
        // else finds first element which is under child
        let elementUnder = null
        if (nextChild) {
          if (nextChild.rect.y > child.rect.y) {
            elementUnder = nextChild
          } else {
            for (let ii = (i + 2); ii < sortedChildren.length; ii++) {
              if (sortedChildren[ii].rect.y > child.rect.y) {
                elementUnder = sortedChildren[ii]
                break
              }
            }
          }
        }
        
        // push childs bounding client rect data
        child.rect = {
          x: child.rect.x,
          y: child.rect.y,
          width: nextChild === null || nextChild.rect.x <= child.rect.x ? child.rect.width : nextChild.rect.x - child.rect.x,
          height: elementUnder ? elementUnder.rect.y - child.rect.y : child.rect.height,
        }

        visibleElementsData.push(child)

        // split area
        // do not split placeholder or empty element
        if (child.element === dropArea.dropAreaEl || this.dropArea !== parent || child.index === this.index || this.index < (child.index-2) || this.index > (child.index+2)) {
          continue
        }

        // decrease index 
        if (this.dropArea === parent && child.index > this.index) {
          child.index--
        }

        // create split child
        const bottomChild = {
          rect: { ...child.rect },
          parent,
          index: child.index + 1,
          element: child.element
        }

        // create bottom child based on drag direction
        if (dropArea.direction === 'y') {
          const halfChildHeight = child.rect.height / 2
          bottomChild.rect.height = child.rect.height = halfChildHeight
          bottomChild.rect.y += halfChildHeight
        } else {
          const halfChildWidth = child.rect.width / 2
          bottomChild.rect.width = child.rect.width = halfChildWidth
          bottomChild.rect.x += halfChildWidth
        }

        visibleElementsData.push(bottomChild)
        
        /*
        if (elementUnder) {
          child.rect.height += elementUnder.rect.height / 2
          elementUnder.rect.y += elementUnder.rect.height / 2
        }
        */
      }

      // finds highest index
      const highestIndex = visibleElementsData.reduce((max, obj) => Math.max(max, obj.index), visibleElementsData[0].index)

      const parentRect = parent.getBoundingClientRect()
      const lastChild = visibleElementsData[visibleElementsData.length - 1]
      const firstChild = visibleElementsData[0]

      // create last child next to rectangle
      if ((lastChild.rect.x + lastChild.rect.width) < (parentRect.x + parentRect.width)) {
        visibleElementsData.push({
          index: highestIndex,
          rect: {
            x: lastChild.rect.x + lastChild.rect.width,
            y: lastChild.rect.y,
            width: (parentRect.width + parentRect.x) - (lastChild.rect.x + lastChild.rect.width),
            height: lastChild.rect.height,
          },
          parent,
          element: parent,
        })
      }

      // create last child bottom rectangle
      if ((lastChild.rect.y + lastChild.rect.height) < (parentRect.y + parentRect.height)) {
        visibleElementsData.push({
          index: highestIndex,
          rect: {
            x: parentRect.x,
            y: lastChild.rect.y + lastChild.rect.height,
            width: parentRect.width,
            height: (parentRect.height + parentRect.y) - (lastChild.rect.y + lastChild.rect.height),
          },
          parent,
          element: parent,
        })
      }

      // create first child top rectangle
      if (firstChild.rect.y > parentRect.y) {
        visibleElementsData.push({
          index: 0,
          rect: {
            x: parentRect.x,
            y: parentRect.y,
            width: parentRect.width,
            height: firstChild.rect.y - parentRect.y,
          },
          parent,
          element: parent,
        })
      }
      
      //visibleElementsData.push(...sortedChildren)
    })

    this.visibleElementsDataValid = true
    this.visibleElementsData = sortBy(visibleElementsData, [(child) => {
      return child.rect.width * child.rect.height
    }, (child) => child.rect.y, (child) => child.rect.x])

    debugRedrawBoundsRect(this.visibleElementsData)
  },

  checkIntersection() {
    if (!this.visibleElementsDataValid) {
      return
    }

    let dropArea = null

    for (let i = 0; i < this.visibleElementsData.length; i++) {
      const child = this.visibleElementsData[i];

      if (child.rect.x <= this.mouseX
        && this.mouseX <= child.rect.x + child.rect.width
        && child.rect.y <= this.mouseY
        && this.mouseY <= child.rect.y + child.rect.height
      ) {
        if (this.dropArea && this.dropArea !== child.parent) {
          this.dropAreas.get(this.dropArea).placeholderIndex.value = null
          this.dropAreas.get(this.dropArea).placeholderEl = null
          this.index = null
        }

        dropArea = child.parent
        //if (this.overElement !== child.element || this.mouseDir + this.index === child.index) {
          if (
            dropArea &&
            this.dropAreas.get(dropArea) &&
            (this.dropAreas.get(dropArea).direction === 'x' && this.mouseX <= child.rect.x + (child.rect.width / 2))
            || (this.dropAreas.get(dropArea).direction === 'y' && this.mouseY <= child.rect.y + (child.rect.height / 2))
          ) {
            this.overElement?.classList?.remove('AwDndOverElement')
            this.overElement = child.element
            this.overElementId.value = child.element.AwDndDragItem?.id
            // for highlighting css selecting overElement
            this.overElement.classList.add('AwDndOverElement')
          
          } else {
            this.overElement?.classList?.remove('AwDndOverElement')
            this.overElementId.value = null
          }

          this.debouncedUpdatePlaceholderIndex(child)
        //}
        break
        //return this.childBoundingRects[i].index
      }
    }

    if (dropArea === null && this.dropArea) {
      this.overElement?.classList?.remove('AwDndOverElement')
      this.overElement = null
      this.overElementId.value = null
      this.debouncedUpdatePlaceholderIndex({
        index: null,
        parent: this.dropArea
      })
    }

    this.dropArea = dropArea
    this.currentDropAreaId.value = this.dropArea?.AwDndDropAreaId
  },

  dragEnd() {
    // remove event listeners
    this.debouncedUpdatePlaceholderIndex.cancel()

    if (!debugMouseDown) {
      this.scrollListener()
      this.mouseMoveListener()
      this.touchMoveListener()
    }

    this.visibleElementsObserver.disconnect()

    if (this.dropArea) {
      this.dropAreas.get(this.dropArea).dropCallback(this.draggedItem.value, this.index)
      this.dropAreas.get(this.dropArea).placeholderIndex.value = null
      this.index = null
      this.dropArea = null
    }

    if (this.overElement) {
      this.overElement.classList.remove('AwDndOverElement')
      this.overElement = null
    }
    
    this.overElementId.value = null
    this.visibleElements.clear()
    this.visibleElementsData = []
    this.visibleElementsDataValid = true
    this.dropAreas.forEach((val, el) => {
      el.ontransitionend = null
      el.ontransitioncancel = null
    })
    this.dropAreas.clear()
    this.mouseX = null
    this.mouseY = null
    this.mouseDir = 0
    debugRedrawBoundsRect([])

    this.draggedItem.value = null
    this.draggedElement = null
    this.originalDropAreaId = null
  },

  autoscroll() {
    if (this.mouseY > (this.windowHeight - 100)) { // auto scroll down
      if (this.autoScrollInterval) {
        return
      }

      this.autoScrollInterval = setInterval(() => {
        const scrollDiff = Math.abs(this.mouseY - (this.windowHeight - 100))

        if((window.scrollY + this.windowHeight + scrollDiff) > this.documentHeight) {
          clearInterval(this.autoScrollInterval)
          this.autoScrollInterval = null
          return
        }

        window.scrollTo(0, window.scrollY + scrollDiff)
      }, 40)
    
    } else if (this.mouseY < 100) { // auto scroll up
      if (this.autoScrollInterval) {
        return
      }

      this.autoScrollInterval = setInterval(() => {
        const scrollDiff = Math.abs(100 - this.mouseY)

        if(window.scrollY - scrollDiff <= 0) {
          clearInterval(this.autoScrollInterval)
          this.autoScrollInterval = null
          window.scrollTo(0, 0)
          return
        }

        window.scrollTo(0, window.scrollY - scrollDiff)
      }, 40)
    
    } else if (this.autoScrollInterval) {
      clearInterval(this.autoScrollInterval)
      this.autoScrollInterval = null
    }
  },

  mouseMove(ev) {
    const posX = ev.touches ? ev.touches[0].clientX : ev.clientX
    const posY = ev.touches ? ev.touches[0].clientY : ev.clientY
    
    this.draggedElement.style.top = (posY - this.draggedElement.awDndOffset.y) + 'px'
    this.draggedElement.style.left = (posX - this.draggedElement.awDndOffset.x) + 'px'
    this.mouseX = posX
    this.mouseY = posY
    this.autoscroll()
    this.recalculateVisibleElements()
    this.checkIntersection()
  },

  registerDropArea($el, getDraggableElements, placeholderIndex, direction, dropAreaEl, dropCallback) {
    $el.ontransitionend = () => {
      this.invalidVisibleElementsData(true)
    }

    $el.ontransitioncancel = () => {
      this.invalidVisibleElementsData(true)
    }
    
    dropAreaEl.awDndDropArea = $el
    this.dropAreas.set($el, {
      placeholderIndex,
      getDraggableElements,
      placeholderEl: null,
      dropAreaEl,
      dropCallback,
      direction,
    })

    // get elements which are not in header or footer and which are not position fixed
    const draggableElements = getDraggableElements()
    
    // observe draggableElements which are displayed
    for (let i = 0; i < draggableElements.length; i++) {
      if (draggableElements[i].style.display === 'none') {
        continue
      }
      this.visibleElementsObserver.observe(draggableElements[i])
    }

    // else observe dropAreaEl
    if (draggableElements.length === 0) {
      this.visibleElementsObserver.observe(dropAreaEl)
    }
  },

  registerDropAreaPlaceholder($el, placeholderEl) {
    //AwDnd.visibleElements.set(placeholderEl, {})
    if (placeholderEl && !placeholderEl.awDndPlaceholder) {
      placeholderEl.awDndPlaceholder = true
      this.visibleElementsObserver.observe(placeholderEl)
      this.dropAreas.get($el).placeholderEl = placeholderEl
    }
  }
}

export default AwDnd
